/**
 * Return-to-job continuity for the generic "Log in" / "Sign up" entry points
 * (navbar, mobile drawer, mobile header CTA, feed gates).
 *
 * Those links used to be bare `/{locale}/login` and `/{locale}/signup`, so
 * `sanitizeCallbackURL` fell back to `/{locale}/seeker` and a visitor who
 * signed up while reading a job never got back to it. PostHog, 30 days to
 * 2026-10-10: of 54 login/signup completions preceded by a job-page view,
 * 37 (69%) next landed on `/seeker` and 9 (17%) on a job page.
 *
 * Only job surfaces (`/{locale}/jobs…`, `/{locale}/remote-jobs…`) get a
 * return path: elsewhere an explicit `callbackURL` would skip the signup
 * role picker (seeker vs business) that bare links rely on. The one exception
 * is the auth pages themselves: "Sign up" on `/login?callbackURL=X` (and the
 * reverse) carries `X` across instead of dropping it.
 *
 * The search query survives too (2026-10-25: of 20 human sessions in 30 days
 * that went from a job page to /login or /signup, 9 had a search or filter in
 * the URL and 0 got back to it). It has to pass Better Auth's relative-callback
 * pattern (`matchesOriginPattern` in `dist/auth/trusted-origins.mjs`) both as
 * sent and after the extra `decodeURIComponent` that `/magic-link/verify`
 * applies, so only parameters whose form-encoded value needs no `%` are kept
 * (letters, digits, `-._` and spaces, which encode as `+`). Anything else is
 * dropped parameter by parameter, as are tracking parameters. Open-redirect
 * safety comes from the same allow-list: the result starts with exactly one
 * `/`, and `:`, `\` and control characters cannot appear in it.
 */

const MAX_LENGTH = 512;
/** One leading `/` (never `//`), then only Better Auth-safe path characters. */
const SAFE_PATH = /^\/(?!\/)[\w\-.+/@]*$/;
/** A `key=value` pair URLSearchParams serialized without any `%` escape. */
const SAFE_PAIR = /^[\w\-.]+=[\w\-.+]*$/;
/** Better Auth's own relative-callback check, mirrored. */
const BETTER_AUTH_RELATIVE =
	/^\/(?!\/|\\|%2f|%5c)[\w\-.+/@]*(?:\?[\w\-.+/=&%@]*)?$/;
const TRACKING_PARAM =
	/^(utm_.*|fbclid|gclid|gbraid|wbraid|msclkid|ttclid|src|ref|_zh|callbackURL)$/;

function isJobSurface(path: string, locale: string): boolean {
	const prefix = `/${locale}/`;
	if (!path.startsWith(prefix)) return false;
	const first = path.slice(prefix.length).split("/")[0];
	return first === "jobs" || first.startsWith("remote-jobs");
}

function isAuthPage(path: string, locale: string): boolean {
	return path === `/${locale}/login` || path === `/${locale}/signup`;
}

function splitUrl(value: string): { path: string; search: string } {
	const hash = value.indexOf("#");
	const noHash = hash === -1 ? value : value.slice(0, hash);
	const q = noHash.indexOf("?");
	return q === -1
		? { path: noHash, search: "" }
		: { path: noHash.slice(0, q), search: noHash.slice(q + 1) };
}

/** The filter/search part of a query that survives the auth round trip. */
function safeQuery(search: string): string {
	if (!search) return "";
	const pairs: string[] = [];
	for (const [key, value] of new URLSearchParams(search)) {
		if (TRACKING_PARAM.test(key) || value === "") continue;
		const pair = new URLSearchParams([[key, value]]).toString();
		if (SAFE_PAIR.test(pair)) pairs.push(pair);
	}
	return pairs.join("&");
}

/** A callback already in flight on an auth page, if it is safe to carry. */
function carriedCallback(search: string): string | null {
	const raw = new URLSearchParams(search).get("callbackURL");
	if (!raw || raw.length > MAX_LENGTH) return null;
	if (!raw.includes("%") && BETTER_AUTH_RELATIVE.test(raw)) return raw;
	const { path } = splitUrl(raw);
	return SAFE_PATH.test(path) ? path : null;
}

/**
 * The same-origin path (plus safe search query) to come back to after auth,
 * or null when the current page is not a job surface or nothing provably safe
 * remains. `url` is a pathname, optionally followed by `?search` / `#hash`.
 */
export function authReturnPath(
	url: string | null | undefined,
	locale: string,
): string | null {
	if (typeof url !== "string" || url.length === 0) return null;
	const { path, search } = splitUrl(url);
	if (path.length > MAX_LENGTH) return null;
	if (!SAFE_PATH.test(path)) return null;
	if (isAuthPage(path, locale)) return carriedCallback(search);
	if (!isJobSurface(path, locale)) return null;
	const query = safeQuery(search);
	const full = query ? `${path}?${query}` : path;
	if (full.length > MAX_LENGTH || !BETTER_AUTH_RELATIVE.test(full)) return path;
	return full;
}

/**
 * `/{locale}/login` or `/{locale}/signup`, carrying `callbackURL` when the
 * visitor is on a job surface. Bare href otherwise (unchanged behaviour).
 */
export function authHref(
	kind: "login" | "signup",
	locale: string,
	url: string | null | undefined,
): string {
	const base = `/${locale}/${kind}`;
	const returnPath = authReturnPath(url, locale);
	return returnPath
		? `${base}?callbackURL=${encodeURIComponent(returnPath)}`
		: base;
}
